import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PlusIcon, Trash2 } from "lucide-react";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { banner_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import {
  useCreateBannerMutation,
  useUpdateBannerMutation,
} from "../../../stores/api/banners.api";
import { buildFormData } from "../../../stores/api/helpers/payload.helper";

const TYPE_OPTIONS = [
  { value: "hero_banner", label: "Hero Banner" },
  { value: "slide", label: "Slide" },
  { value: "event", label: "Event" },
];

const POSITION_OPTIONS = [
  { value: "left_bottom",   label: "Left Bottom" },
  { value: "left_top",      label: "Left Top" },
  { value: "left",          label: "Left Center" },
  { value: "right_bottom",  label: "Right Bottom" },
  { value: "right_top",     label: "Right Top" },
  { value: "right",         label: "Right Center" },
  { value: "center_bottom", label: "Center Bottom" },
  { value: "center_top",    label: "Center Top" },
  { value: "center",        label: "Center" },
];

// Screen-size breakpoints (value = max-width px used for <picture> media query)
const SCREEN_OPTIONS = [
  { value: "640", label: "Mobile (≤640px)" },
  { value: "768", label: "Tablet (≤768px)" },
  { value: "1024", label: "Laptop (≤1024px)" },
  { value: "1920", label: "Desktop (≤1920px)" },
];

const emptyVariant = () => ({ size: "", image_alt: "", file: null, previewUrl: null });

const EMPTY = {
  title: "",
  badge: "",
  link: "",
  short_description: "",
  button_text: "",
  button_color: "#ff6600",
  status: false,
  type: "hero_banner",
  data_position: "left_bottom",
  start_date: "",
  end_date: "",
};

const CreateBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.banner;

  const [createBanner, { isLoading: creating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: updating }] = useUpdateBannerMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
          title: details.title || "",
          badge: details.badge || "",
          link: details.link || "",
          short_description: details.short_description || "",
          button_text: details.button_text || "",
          button_color: details.button_color || "#ff6600",
          status: details.status ?? false,
          type: details.type || "hero_banner",
          data_position: details.data_position || "left_bottom",
          start_date: details.start_date ? details.start_date.slice(0, 10) : "",
          end_date: details.end_date ? details.end_date.slice(0, 10) : "",
        }
      : {}),
  });

  // Seed variant rows from existing image_varients on edit, else one empty row.
  const seedVariants = () => {
    let v = details?.image_varients;
    // MySQL JSON column may come back as a string — parse defensively.
    if (typeof v === "string") {
      try { v = JSON.parse(v); } catch { v = null; }
    }
    if (v && typeof v === "object" && Object.keys(v).length) {
      return Object.entries(v).map(([size, entry]) => ({
        size: String(size),
        image_alt: entry.image_alt ?? "",
        file: null,
        previewUrl: entry.path ? (banner_image_url ?? "") + entry.path : null,
      }));
    }
    return [emptyVariant()];
  };

  const [variants, setVariants] = useState(seedVariants);

  const setVariant = (idx, patch) =>
    setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  const addVariant = () => setVariants((prev) => [...prev, emptyVariant()]);
  const removeVariant = (idx) =>
    setVariants((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const resetForm = () => {
    setFormData(EMPTY);
    setVariants([emptyVariant()]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate variants: each row needs a screen size; new banners need a file per row.
    const validRows = variants.filter((v) => v.size);
    if (validRows.length === 0) {
      ToastMessage({ type: "error", title: "Validation", message: "Add at least one image variant with a screen size." });
      return;
    }
    const sizes = validRows.map((v) => v.size);
    if (new Set(sizes).size !== sizes.length) {
      ToastMessage({ type: "error", title: "Validation", message: "Each screen size can be used only once." });
      return;
    }
    const newFiles = validRows.filter((v) => v.file);
    if (!details && newFiles.length !== validRows.length) {
      ToastMessage({ type: "error", title: "Validation", message: "Upload an image for every variant." });
      return;
    }

    try {
      const fields = {
        ...formData,
        status: formData.status ? 1 : 0,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      };

      // Send files + variants_meta (index-aligned) only when new files are present.
      const sendVariants = newFiles.length > 0;
      if (sendVariants) {
        const fileRows = validRows.filter((v) => v.file);
        fields.images = fileRows.map((v) => v.file);
        fields.variants_meta = JSON.stringify(
          fileRows.map((v) => ({ size: v.size, image_alt: v.image_alt || "" }))
        );
      }

      const body = buildFormData(fields);

      if (details) {
        await updateBanner({ id: details.id, body }).unwrap();
      } else {
        await createBanner(body).unwrap();
      }

      ToastMessage({ type: "success", title: "Success", message: details ? "Banner updated!" : "Banner created!" });
      if (!details) navigate(ROUTE_PATHS.BANNER);
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Banner" : "Create Banner"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Banner Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Title" name="title" value={formData.title} onChange={(val) => setField("title", val)} placeholder="Enter banner title" />
            <FormInput label="Badge (optional)" name="badge" value={formData.badge} onChange={(val) => setField("badge", val)} placeholder="e.g. Premium Picks, New Arrival" />
            <FormInput label="Link URL" name="link" value={formData.link} onChange={(val) => setFormData((prev) => ({ ...prev, link: val }))} placeholder="https://example.com" />
            <FormInput label="Short Description" name="short_description" value={formData.short_description} onChange={(val) => setFormData((prev) => ({ ...prev, short_description: val }))} placeholder="Brief description" rows={2} />
            <FormInput label="Button Text" name="button_text" value={formData.button_text} onChange={(val) => setFormData((prev) => ({ ...prev, button_text: val }))} placeholder="e.g. Shop Now" />
            <div>
              <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">Button Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={formData.button_color} onChange={(e) => setFormData((prev) => ({ ...prev, button_color: e.target.value }))} className="h-10 w-16 rounded cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800" />
                <span className="text-sm text-gray-500 dark:text-gray-400">{formData.button_color}</span>
              </div>
            </div>
            <FormSelect label="Type" required options={TYPE_OPTIONS} value={formData.type} onChange={(val) => setFormData((prev) => ({ ...prev, type: val ?? "hero_banner" }))} />
            <FormSelect label="Content Position" options={POSITION_OPTIONS} value={formData.data_position} onChange={(val) => setField("data_position", val ?? "left_bottom")} />
          </div>
        </FormSection>

        {formData.type === "event" && <FormSection title="Schedule (optional)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Start Date" name="start_date" type="date" value={formData.start_date} onChange={(val) => setFormData((prev) => ({ ...prev, start_date: val }))} />
            <FormInput label="End Date" name="end_date" type="date" value={formData.end_date} onChange={(val) => setFormData((prev) => ({ ...prev, end_date: val }))} />
          </div>
        </FormSection>}

        <FormSection title="Banner Images (Responsive Variants)">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Add an image per screen size. The smallest is used as the default fallback; at least one is required.
          </p>
          <div className="space-y-4">
            {variants.map((v, idx) => (
              <div
                key={idx}
                className="relative border border-gray-200 dark:border-slate-700 rounded-lg p-4 space-y-4"
              >
                {/* Remove button (top-right) */}
                <Button
                  type="button"
                  icon={Trash2}
                  disabled={variants.length <= 1}
                  onClick={() => removeVariant(idx)}
                  title="Remove"
                  className="!absolute !top-2 !right-2 !bg-red-50 !text-red-600 hover:!bg-red-100 disabled:!opacity-40 dark:!bg-red-500/10 dark:!text-red-300"
                />

                {/* Row 1: screen size + alt text */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-12">
                  <FormSelect
                    label="Screen Size"
                    required
                    isClearable={false}
                    options={SCREEN_OPTIONS.filter(
                      (opt) => opt.value === v.size || !variants.some((other, i) => i !== idx && other.size === opt.value)
                    )}
                    value={v.size}
                    onChange={(val) => setVariant(idx, { size: val ?? "" })}
                  />
                  <FormInput
                    label="Image Alt Text"
                    name={`image_alt_${idx}`}
                    value={v.image_alt}
                    onChange={(val) => setVariant(idx, { image_alt: val })}
                    placeholder="Describe the image"
                  />
                </div>

                {/* Row 2: image dropzone (full width) */}
                <FormImage
                  label="Image"
                  hint="Recommended size: 1520×600px (38:15 aspect ratio)"
                  required={!details}
                  initialPreviewUrl={v.previewUrl}
                  onChange={(file) => setVariant(idx, { file })}
                />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Button
              type="button"
              icon={PlusIcon}
              onClick={addVariant}
              className="!bg-blue-100 !text-blue-600 hover:!bg-blue-600 hover:!text-white dark:!bg-blue-500/10 dark:!text-blue-300"
            >
              Add Screen Size
            </Button>
          </div>
        </FormSection>

        <FormSection title="Status">
          <FormSwitch label="Status" checked={!!formData.status} onChange={(val) => setFormData((prev) => ({ ...prev, status: val }))} />
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" disabled={loading} onClick={() => { resetForm(); navigate(ROUTE_PATHS.BANNER); }} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200">
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading} className="!bg-orange-500 !text-white hover:!bg-orange-600" loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Banner" : "Create Banner"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBanner;
